import React, { forwardRef } from "react";
import { useAppSettings, resolveImageUrl } from "../context/AppSettingsContext";
import { Flame } from "lucide-react";

const LoyaltyCard = forwardRef(({ customer, size = "normal", branchBgUrl = null }, ref) => {
  const { settings } = useAppSettings();
  const s = settings;
  const logo = resolveImageUrl(s.logo_url);
  const effectiveBgUrl = branchBgUrl || s.card_bg_image_url || null;
  const bgImage = resolveImageUrl(effectiveBgUrl);
  const isDarkText = (() => { const h = (s.card_text || "#F4F4F5").replace("#", ""); if (h.length < 6) return false; const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)); return (0.299 * r + 0.587 * g + 0.114 * b) < 128; })();
  const cardNumber = customer?.card_number || "•••• •••• ••••";
  const name = customer?.name || "Nome Cognome";
  const points = customer?.points ?? 0;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&bgcolor=ffffff&color=${encodeURIComponent((bgImage ? "#0F172A" : s.card_bg).replace("#",""))}&data=${encodeURIComponent(cardNumber)}`;
  return (
    <div
      ref={ref}
      className="loyalty-card relative w-full rounded-2xl overflow-hidden shrink-0"
      style={{
        aspectRatio: "1.586 / 1",
        background: bgImage
          ? `#000`
          : `linear-gradient(135deg, ${s.card_bg} 0%, ${adjust(s.card_bg, -12)} 100%)`,
        color: s.card_text,
        "--card-text": s.card_text,
        boxShadow: `0 20px 60px -20px ${s.primary_color}55, inset 0 1px 0 ${withAlpha(s.card_text, 0.08)}`,
      }}
      data-testid="loyalty-card"
    >
      {bgImage && (
        <>
          <img
            src={bgImage}
            alt="branch card graphic"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Overlay for readability: dark for light text, white for dark text (card graphics on white) */}
          <div className={`absolute inset-0 bg-gradient-to-br ${isDarkText ? "from-white/80 via-white/45 to-white/80" : "from-black/70 via-black/40 to-black/70"}`} />
        </>
      )}
      {/* corner accent */}
      <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full opacity-40 blur-2xl"
           style={{ background: s.primary_color }} />
      <div className="absolute -bottom-20 -left-10 h-40 w-40 rounded-full opacity-25 blur-2xl"
           style={{ background: s.accent_color }} />

      <div className="relative p-5 sm:p-6 h-full flex flex-col justify-between">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {logo ? (
              <img src={logo} alt="logo" className="h-10 w-10 rounded-lg object-cover ring-1 ring-white/10" />
            ) : (
              <div className="h-10 w-10 rounded-lg flex items-center justify-center"
                   style={{ background: s.primary_color }}>
                <Flame className="h-5 w-5" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-heading font-bold tracking-wide text-sm uppercase leading-tight truncate">
                {s.app_name}
              </div>
              <div className="text-[10px] opacity-70 tracking-widest uppercase">Loyalty Card</div>
            </div>
          </div>
          <img src={qrUrl} alt="QR" className="h-16 w-16 sm:h-20 sm:w-20 rounded-md bg-white p-0.5 shadow-md" />
        </div>

        <div className="space-y-1">
          <div className="text-[10px] opacity-70 tracking-widest uppercase">Titolare</div>
          <div className="font-heading text-lg sm:text-2xl font-bold truncate">{name}</div>
        </div>

        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="text-[10px] opacity-70 tracking-widest uppercase">N° Carta</div>
            <div className="font-mono text-base sm:text-lg font-semibold tracking-widest">
              {formatCard(cardNumber)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] opacity-70 tracking-widest uppercase">Punti</div>
            <div className="font-heading text-2xl sm:text-3xl font-extrabold">{points}</div>
          </div>
        </div>

        {s.card_tagline && size !== "compact" && (
          <div className="absolute bottom-1 left-5 right-5 text-[10px] opacity-60 truncate">
            {s.card_tagline}
          </div>
        )}
      </div>
    </div>
  );
});

function formatCard(n) {
  const clean = String(n || "").replace(/\s/g, "");
  return clean.replace(/(.{4})/g, "$1 ").trim();
}
function withAlpha(hex, a) {
  const c = hex.replace("#", "");
  const num = parseInt(c.length === 3 ? c.split("").map((x) => x + x).join("") : c, 16);
  const r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  return `rgba(${r},${g},${b},${a})`;
}
function adjust(hex, delta) {
  const c = hex.replace("#", "");
  const num = parseInt(c.length === 3 ? c.split("").map((x) => x + x).join("") : c, 16);
  let r = ((num >> 16) & 255) + delta;
  let g = ((num >> 8) & 255) + delta;
  let b = (num & 255) + delta;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

LoyaltyCard.displayName = "LoyaltyCard";
export default LoyaltyCard;
